/**
 * pages/AcademyPage.tsx
 * Route: /academy
 * Jötr Academy — Student workspace with subjects and canvas notebooks
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, BookOpen, Trash2, ChevronRight, X, Loader, GraduationCap, Pencil } from 'lucide-react';
import { useStore } from '../store';
import { useAuth } from '../hooks';
import { toast } from '../components/ui/Toast';
import { useNavigate } from 'react-router-dom';
import { getToken } from '../api/client';

const C = {
  amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A', amber500:'#8B5A0A',
  ink:'#1A1814', inkLight:'#3D3A34', muted:'#7A7570', border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF',
  red:'#DC2626', red100:'#FEF2F2',
};
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
// getToken imported from client.ts
const headers = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` });

interface Subject {
  id: string; name: string; code: string; color: string;
  icon: string; emoji: string; description: string;
  pageCount: number; createdAt: string;
}

const subjectsApi = {
  list:   ()              => fetch(`${API}/subjects`, { headers: headers() }).then(r => r.json()),
  create: (body: any)     => fetch(`${API}/subjects`, { method:'POST', headers: headers(), body: JSON.stringify(body) }).then(r => r.json()),
  update: (id: string, body: any) => fetch(`${API}/subjects/${id}`, { method:'PATCH', headers: headers(), body: JSON.stringify(body) }).then(r => r.json()),
  delete: (id: string)    => fetch(`${API}/subjects/${id}`, { method:'DELETE', headers: headers() }),
};

const COLORS = ['#F5A623','#C4811A','#16A34A','#7C3AED','#DB2777','#2563EB','#DC2626','#0891B2','#059669','#D97706'];

const AcademyPage: React.FC = () => {
  const { isAuthenticated, authLoading } = useAuth();
  const initAuth = useStore(s => s.initAuth);
  const navigate = useNavigate();

  const [subjects,    setSubjects]    = useState<Subject[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [showForm,    setShowForm]    = useState(false);
  const [editSubject, setEditSubject] = useState<Subject | null>(null);
  const [saving,      setSaving]      = useState(false);
  const [deleting,    setDeleting]    = useState<string | null>(null);

  // Form state
  const [name,        setName]        = useState('');
  const [code,        setCode]        = useState('');
  const [desc,        setDesc]        = useState('');
  const [color,       setColor]       = useState(COLORS[0]);

  useEffect(() => { initAuth(); }, []); // eslint-disable-line

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await subjectsApi.list();
      if (Array.isArray(data)) setSubjects(data);
    } catch { toast.error('Could not load subjects'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isAuthenticated) load(); }, [isAuthenticated, load]);

  const openForm = (subject?: Subject) => {
    if (subject) {
      setEditSubject(subject);
      setName(subject.name);
      setCode(subject.code || '');
      setDesc(subject.description || '');
      setColor(subject.color);
    } else {
      setEditSubject(null);
      setName(''); setCode(''); setDesc(''); setColor(COLORS[subjects.length % COLORS.length]);
    }
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false); setEditSubject(null);
    setName(''); setCode(''); setDesc(''); setColor(COLORS[0]);
  };

  const saveSubject = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (editSubject) {
        const updated = await subjectsApi.update(editSubject.id, { name: name.trim(), code: code.trim(), description: desc.trim(), color });
        setSubjects(prev => prev.map(s => s.id === editSubject.id ? updated : s));
        toast.success('Subject updated!');
      } else {
        const created = await subjectsApi.create({ name: name.trim(), code: code.trim(), description: desc.trim(), color });
        if (created?.id) {
          setSubjects(prev => [created, ...prev]);
          toast.success('Subject created!');
        }
      }
      closeForm();
    } catch { toast.error('Could not save subject'); }
    finally { setSaving(false); }
  };

  const deleteSubject = async (id: string) => {
    setDeleting(id);
    try {
      await subjectsApi.delete(id);
      setSubjects(prev => prev.filter(s => s.id !== id));
      toast.success('Subject deleted');
    } catch { toast.error('Could not delete subject'); }
    finally { setDeleting(null); }
  };

  if (authLoading || loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:C.surface, flexDirection:'column', gap:16 }}>
      <Loader size={32} color={C.amber300} style={{ animation:'spin .7s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <p style={{ fontSize:14, color:C.muted, fontFamily:font.body }}>Loading Academy…</p>
    </div>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        body{font-family:'DM Sans',sans-serif;background:${C.surface};}
        .acad-input{width:100%;padding:10px 14px;border-radius:10px;border:1.5px solid ${C.border};font-size:14px;font-family:'DM Sans',sans-serif;color:${C.ink};outline:none;background:${C.white};transition:border-color .15s;}
        .acad-input:focus{border-color:${C.amber300};box-shadow:0 0 0 3px ${C.amber300}22;}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes slideUp{from{opacity:0;transform:translateY(40px)}to{opacity:1;transform:translateY(0)}}
      `}</style>

      <div style={{ minHeight:'100vh', background:C.surface }}>

        {/* Top bar */}
        <div style={{ background:C.white, borderBottom:`1px solid ${C.border}`, padding:'0 clamp(16px,3vw,28px)', height:60, display:'flex', alignItems:'center', gap:14, position:'sticky', top:0, zIndex:40 }}>
          <a href="/dashboard" style={{ display:'flex', alignItems:'center', gap:8, textDecoration:'none' }}>
            <div style={{ width:30, height:30, borderRadius:9, background:`linear-gradient(145deg,${C.amber200},${C.amber400})`, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="13" height="13" viewBox="0 0 180 180" fill="none">
                <g transform="translate(90,88) rotate(-35)"><rect x="-12" y="-38" width="24" height="58" rx="7" fill="white"/><rect x="-12" y="-38" width="24" height="16" rx="7" fill="#F08030" opacity="0.9"/><polygon points="-12,20 12,20 0,44" fill="white" opacity="0.9"/></g>
                <circle cx="104" cy="102" r="42" fill="none" stroke="white" strokeWidth="13" opacity="0.95"/>
                <g transform="translate(126,124) rotate(45)"><rect x="-6" y="-4" width="30" height="10" rx="5" fill="white"/></g>
              </svg>
            </div>
            <span style={{ fontFamily:font.head, fontWeight:800, fontSize:18, letterSpacing:'-0.05em', color:C.ink }}>jötr</span>
          </a>
          <div style={{ display:'flex', alignItems:'center', gap:6, flex:1 }}>
            <ChevronRight size={14} color={C.muted}/>
            <span style={{ fontSize:14, fontWeight:700, color:C.ink, fontFamily:font.body }}>Academy</span>
          </div>
          <button onClick={() => openForm()}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:10, border:'none', background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:14, fontFamily:font.body, cursor:'pointer', boxShadow:`0 4px 12px ${C.amber300}44` }}>
            <Plus size={15}/> New subject
          </button>
        </div>

        <div style={{ maxWidth:800, margin:'0 auto', padding:'32px clamp(16px,3vw,28px) 80px' }}>

          {/* Hero */}
          <div style={{ marginBottom:32, display:'flex', alignItems:'flex-start', gap:16 }}>
            <div style={{ width:56, height:56, borderRadius:16, background:`linear-gradient(145deg,${C.amber200},${C.amber400})`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <GraduationCap size={28} color={C.white}/>
            </div>
            <div>
              <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:28, letterSpacing:'-0.04em', color:C.ink, marginBottom:4 }}>
                Jötr Academy
              </h1>
              <p style={{ fontSize:14, color:C.muted, fontFamily:font.body, lineHeight:1.6 }}>
                Your student workspace. Organise notes by subject, write on digital notebooks and annotate your lecture slides.
              </p>
            </div>
          </div>

          {/* Subjects grid */}
          {subjects.length === 0 ? (
            <div style={{ textAlign:'center', padding:'60px 20px', background:C.white, borderRadius:20, border:`1px dashed ${C.border}` }}>
              <div style={{ fontSize:48, marginBottom:16 }}>📚</div>
              <h2 style={{ fontFamily:font.head, fontWeight:800, fontSize:22, color:C.ink, marginBottom:8 }}>No subjects yet</h2>
              <p style={{ fontSize:14, color:C.muted, fontFamily:font.body, marginBottom:24, lineHeight:1.7 }}>
                Create your first subject to get started.<br/>Add course codes like MCE101 or BUS202.
              </p>
              <button onClick={() => openForm()}
                style={{ padding:'12px 28px', borderRadius:12, border:'none', background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:15, fontFamily:font.body, cursor:'pointer' }}>
                <Plus size={15} style={{ marginRight:6, display:'inline', verticalAlign:'middle' }}/>Create subject
              </button>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(220px, 1fr))', gap:16 }}>
              {subjects.map(subject => (
                <SubjectCard
                  key={subject.id}
                  subject={subject}
                  onOpen={() => navigate(`/academy/${subject.id}`)}
                  onEdit={() => openForm(subject)}
                  onDelete={() => deleteSubject(subject.id)}
                  deleting={deleting === subject.id}
                />
              ))}
              {/* Add subject card */}
              <button onClick={() => openForm()}
                style={{ background:'none', border:`2px dashed ${C.border}`, borderRadius:20, padding:'32px 20px', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:10, transition:'border-color .15s, background .15s' }}
                onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.borderColor=C.amber300;(e.currentTarget as HTMLButtonElement).style.background=C.amber100;}}
                onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.borderColor=C.border;(e.currentTarget as HTMLButtonElement).style.background='none';}}>
                <Plus size={24} color={C.muted}/>
                <span style={{ fontSize:13, fontWeight:600, color:C.muted, fontFamily:font.body }}>Add subject</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit form modal */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'flex-end', justifyContent:'center' }}
          onClick={e => { if (e.target === e.currentTarget) closeForm(); }}>
          <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(4px)' }} onClick={closeForm}/>
          <div style={{ position:'relative', width:'min(100%,520px)', background:C.white, borderRadius:'20px 20px 0 0', padding:'24px 24px 32px', boxShadow:'0 -8px 40px rgba(0,0,0,0.2)', animation:'slideUp .3s ease', zIndex:1, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
              <h2 style={{ fontFamily:font.head, fontWeight:800, fontSize:20, color:C.ink }}>
                {editSubject ? 'Edit subject' : 'New subject'}
              </h2>
              <button onClick={closeForm} style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, padding:4, display:'flex' }}><X size={18}/></button>
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {/* Subject name */}
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:C.inkLight, fontFamily:font.body, display:'block', marginBottom:5 }}>Subject name *</label>
                <input className="acad-input" value={name} onChange={e=>setName(e.target.value)}
                  placeholder="e.g. Engineering Mechanics" autoFocus/>
              </div>

              {/* Course code */}
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:C.inkLight, fontFamily:font.body, display:'block', marginBottom:5 }}>
                  Course code <span style={{ color:C.muted, fontWeight:400 }}>(optional)</span>
                </label>
                <input className="acad-input" value={code} onChange={e=>setCode(e.target.value.toUpperCase())}
                  placeholder="e.g. MCE101"/>
              </div>

              {/* Description */}
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:C.inkLight, fontFamily:font.body, display:'block', marginBottom:5 }}>
                  Description <span style={{ color:C.muted, fontWeight:400 }}>(optional)</span>
                </label>
                <input className="acad-input" value={desc} onChange={e=>setDesc(e.target.value)}
                  placeholder="e.g. 200 Level, Dr. Adeyemi"/>
              </div>

              {/* Color picker */}
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:C.inkLight, fontFamily:font.body, display:'block', marginBottom:8 }}>Colour</label>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {COLORS.map(c => (
                    <button key={c} onClick={() => setColor(c)}
                      style={{ width:32, height:32, borderRadius:'50%', background:c, border:color===c?`3px solid ${C.ink}`:`3px solid transparent`, cursor:'pointer', transition:'transform .1s' }}
                      onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.transform='scale(1.15)'}
                      onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.transform='scale(1)'}/>
                  ))}
                </div>
              </div>

              {/* Preview */}
              {name && (
                <div style={{ background:C.surface, borderRadius:12, padding:'12px 14px', display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:40, height:40, borderRadius:12, background:color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>
                    📚
                  </div>
                  <div>
                    <p style={{ fontSize:14, fontWeight:700, color:C.ink, fontFamily:font.body }}>{name}</p>
                    {code && <p style={{ fontSize:11, color:C.muted, fontFamily:font.body }}>{code}</p>}
                  </div>
                </div>
              )}

              <button onClick={saveSubject} disabled={saving || !name.trim()}
                style={{ height:46, borderRadius:12, border:'none', background:name.trim()?`linear-gradient(135deg,${C.amber300},${C.amber400})`:'#ccc', cursor:name.trim()?'pointer':'not-allowed', fontFamily:font.body, fontWeight:700, fontSize:15, color:C.white, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                {saving ? <Loader size={16} style={{ animation:'spin .7s linear infinite' }}/> : <BookOpen size={16}/>}
                {saving ? 'Saving…' : editSubject ? 'Save changes' : 'Create subject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ── Subject card ──────────────────────────────────────────────────────────────
const SubjectCard: React.FC<{
  subject: Subject;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}> = ({ subject, onOpen, onEdit, onDelete, deleting }) => (
  <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:20, overflow:'hidden', cursor:'pointer', transition:'box-shadow .15s, transform .15s', animation:'fadeIn .25s ease' }}
    onMouseEnter={e=>{(e.currentTarget as HTMLDivElement).style.boxShadow='0 8px 28px rgba(0,0,0,0.1)';(e.currentTarget as HTMLDivElement).style.transform='translateY(-2px)';}}
    onMouseLeave={e=>{(e.currentTarget as HTMLDivElement).style.boxShadow='none';(e.currentTarget as HTMLDivElement).style.transform='';}}>

    {/* Colour header */}
    <div onClick={onOpen} style={{ height:80, background:`linear-gradient(135deg,${subject.color}dd,${subject.color})`, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 16px', position:'relative' }}>
      <span style={{ fontSize:36 }}>{subject.emoji}</span>
      <div style={{ textAlign:'right' }}>
        {subject.code && (
          <span style={{ fontSize:11, fontWeight:800, color:C.white, fontFamily:font.head, background:'rgba(0,0,0,0.2)', padding:'3px 8px', borderRadius:20, letterSpacing:'0.05em' }}>
            {subject.code}
          </span>
        )}
      </div>
    </div>

    {/* Content */}
    <div style={{ padding:'14px 16px' }}>
      <div onClick={onOpen}>
        <h3 style={{ fontSize:14, fontWeight:700, color:C.ink, fontFamily:font.head, marginBottom:4, lineHeight:1.3 }}>{subject.name}</h3>
        {subject.description && (
          <p style={{ fontSize:12, color:C.muted, fontFamily:font.body, marginBottom:8, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{subject.description}</p>
        )}
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <Pencil size={11} color={C.muted}/>
          <span style={{ fontSize:11, color:C.muted, fontFamily:font.body }}>
            {subject.pageCount} {subject.pageCount === 1 ? 'page' : 'pages'}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display:'flex', gap:6, marginTop:12, paddingTop:12, borderTop:`1px solid ${C.border}` }}>
        <button onClick={onOpen}
          style={{ flex:1, height:34, borderRadius:8, border:'none', background:`linear-gradient(135deg,${subject.color}22,${subject.color}33)`, color:subject.color, fontWeight:700, fontSize:12, fontFamily:font.body, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
          <BookOpen size={12}/> Open
        </button>
        <button onClick={onEdit}
          style={{ width:34, height:34, borderRadius:8, border:`1px solid ${C.border}`, background:C.surface, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:C.muted }}>
          <Pencil size={12}/>
        </button>
        <button onClick={onDelete} disabled={deleting}
          style={{ width:34, height:34, borderRadius:8, border:`1px solid ${C.border}`, background:C.surface, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:deleting?C.muted:C.red }}>
          {deleting ? <Loader size={12} style={{ animation:'spin .7s linear infinite' }}/> : <Trash2 size={12}/>}
        </button>
      </div>
    </div>
  </div>
);

export default AcademyPage;